<template>
    <Modal ref="modalRef" :secondary-button-label="trans('general.action.close')" :show-primary-button="false" max-width="5xl">
        <template #title>
            <div>{{ task.taskGroup?.label }}:&nbsp;{{ task.title }}</div>
        </template>

        <div>
            <task-approval-form
                v-if="task.approval.required && !task.approval.approvedAt && canApprove"
                :approver-id="approverId"
                :approvers="approvers?.data || []"
                :disabled="!allowApproverSelection"
                :submit-label="createWorkOrderOnApproval ? trans('tasks.supervisor.approval.button_with_workorder') : trans('general.action.approve')"
                :task-id="task.id"
                class="sticky -top-4 z-10 -mx-8 -mt-4"
                @approved="onTaskApproved"
            />

            <!-- Snapshot notice: shown whenever the view is built from the immutable snapshot
                 (performed / approved / lapsed-overdue) rather than the live task. A lapsed-overdue
                 snapshot was never executed, so it gets wording about when the task lapsed. -->
            <alert v-if="task.taskSnapshotRecordId" class="mt-4 mb-0" color="info" data-testid="task-snapshot-notice">
                {{ task.resolution?.value === 'overdue' ? trans('tasks.details.snapshot.notice_overdue') : trans('tasks.details.snapshot.notice') }}
            </alert>

            <!-- Asset Data -->
            <section class="mt-5 rounded-xl p-3" style="background: #f8f8f8; border: 1px solid var(--accent-grey, #ced2d8)">
                <h2 class="mb-2 ps-2 !text-base !font-bold">
                    {{ trans('tasks.details.asset_data.label') }}
                </h2>
                <div class="justify-between gap-2 sm:flex">
                    <table class="table-unstyled">
                        <tbody>
                            <tr v-for="data in assetData" :key="data.label">
                                <th class="align-top">
                                    <div class="pe-2">{{ data.label }}</div>
                                </th>
                                <td>{{ data.value ?? '-' }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="flex items-end justify-end p-2">
                        <qrcode-vue :value="`https://id.curie.network/${task.asset.curieId}`" background="#f8f8f8" />
                    </div>
                </div>
            </section>

            <!-- Test Data -->
            <section class="mt-4 p-3">
                <h2 class="mb-2 ps-2 !text-base !font-bold">
                    {{ trans('tasks.details.test_data.label') }}
                </h2>
                <div>
                    <table class="table-unstyled">
                        <tbody>
                            <tr>
                                <th>{{ trans('tasks.details.ticket_id.label') }}</th>
                                <td>{{ task.code }}</td>
                            </tr>
                            <tr>
                                <th>{{ trans('tasks.details.date.label') }}</th>
                                <td>{{ formatTaskDate(task.resolvedAt || task.dueAt) }}</td>
                            </tr>
                            <tr>
                                <th>{{ trans('tasks.details.time.label') }}</th>
                                <td>{{ formatTaskTime(task.resolvedAt || task.dueAt) }}</td>
                            </tr>
                            <tr>
                                <th>{{ trans('tasks.details.user.label') }}</th>
                                <td>{{ task.performedBy?.name || '-' }}</td>
                            </tr>
                            <tr>
                                <th>{{ trans('tasks.details.qr.label') }}</th>
                                <td>
                                    <div class="flex items-center gap-2">
                                        {{
                                            task.presenceDetection?.scanned_at
                                                ? trans('test_records.overview.tooltip.qr_code_scanned')
                                                : trans('test_records.overview.tooltip.qr_code_not_scanned')
                                        }}
                                    </div>
                                </td>
                            </tr>

                            <tr v-if="task.approval?.required">
                                <th>{{ trans('tasks.details.approval.label') }}</th>
                                <td>
                                    <template v-if="task.approval.approvedBy">
                                        {{ task.approval.approvedBy.last_name }}
                                        {{ trans('general.time.at') }}
                                        {{ formatTaskTime(task.approval.approvedAt) }}
                                        ({{ formatTaskDate(task.approval.approvedAt) }})
                                    </template>
                                    <template v-else>-</template>
                                </td>
                            </tr>

                            <tr v-if="task.relatedWorkOrder">
                                <th>{{ trans('tasks.details.work_order.label') }}</th>
                                <td>
                                    <a
                                        v-if="task.relatedWorkOrder.canView"
                                        class="text-[#002377] underline"
                                        data-testid="task-related-workorder-link"
                                        href="#"
                                        @click.prevent="openWorkOrder(task.relatedWorkOrder.url)"
                                    >
                                        {{ task.relatedWorkOrder.code }}
                                    </a>
                                    <span v-else>{{ task.relatedWorkOrder.code }}</span>
                                </td>
                            </tr>
                            <tr v-if="task.relatedWorkOrder?.mspRespondedAt">
                                <th>{{ trans('workorder.details.workorder.msp_responded_at.label') }}</th>
                                <td>{{ formatRelatedWoTime(task.relatedWorkOrder.mspRespondedAt) }}</td>
                            </tr>
                            <tr v-if="task.relatedWorkOrder?.catsaCalledAt">
                                <th>{{ trans('workorder.details.workorder.catsa_called_at.label') }}</th>
                                <td>{{ formatRelatedWoTime(task.relatedWorkOrder.catsaCalledAt) }}</td>
                            </tr>
                            <tr v-if="task.relatedWorkOrder?.backInServiceAt">
                                <th>{{ trans('workorder.details.workorder.back_in_service_at.label') }}</th>
                                <td>{{ formatRelatedWoTime(task.relatedWorkOrder.backInServiceAt) }}</td>
                            </tr>

                            <tr>
                                <th>{{ trans('tasks.details.result.label') }}</th>
                                <td>
                                    <div :style="{ color: task.resolution?.textColor }" class="flex items-center gap-2">
                                        {{ task.resolution?.label }}
                                        <span v-if="task.type.value === 'manual_reintegration_attestation'" class="text-muted">
                                            ({{ trans('tasks.details.generated.label') }})
                                        </span>
                                    </div>
                                </td>
                            </tr>

                            <!-- Task type-specific cases -->
                            <template v-if="task.type.value === 'asset_location_check'">
                                <tr>
                                    <th>{{ trans('tasks.details.test_type.label') }}</th>
                                    <td>{{ task.type?.label ?? '-' }}</td>
                                </tr>
                                <tr>
                                    <th>{{ trans('tasks.details.location.label') }}</th>
                                    <td>
                                        <template v-if="task.resolution?.value === 'updated'">
                                            <span style="text-decoration: line-through">
                                                {{ getLocationHierarchy(task.payload.oldLocation) }}
                                            </span>
                                            &nbsp;&nbsp;→&nbsp;&nbsp;
                                            {{ getLocationHierarchy(task.payload.newLocation) }}
                                        </template>
                                        <template v-else>
                                            {{ getLocationHierarchy(task.payload.oldLocation) }}
                                        </template>
                                    </td>
                                </tr>
                            </template>

                            <template v-if="task.type.value === 'asset_status_check'">
                                <tr>
                                    <th>{{ trans('tasks.details.test_type.label') }}</th>
                                    <td>{{ task.type?.label ?? '-' }}</td>
                                </tr>
                                <tr>
                                    <th>{{ trans('tasks.details.status.label') }}</th>
                                    <td>
                                        <div v-if="task.payload" class="flex justify-start gap-1">
                                            <template v-if="task.resolution?.value === 'updated'">
                                                <asset-status-label
                                                    :operability="task.payload.currentOperability"
                                                    :strike-through="true"
                                                    :usage-category="task.payload.currentUsageCategory"
                                                />&nbsp;→&nbsp;<asset-status-label
                                                    :operability="task.payload.newOperability"
                                                    :usage-category="task.payload.newUsageCategory"
                                                />
                                            </template>
                                            <template v-else>
                                                <asset-status-label
                                                    :operability="task.payload.currentOperability"
                                                    :usage-category="task.payload.currentUsageCategory"
                                                />
                                            </template>
                                        </div>
                                        <div v-else>-</div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>{{ trans('tasks.details.reason.label') }}</th>
                                    <td>{{ task.payload?.changeReason?.label ?? '-' }}</td>
                                </tr>
                            </template>

                            <template v-if="['asset_throughput_metric', 'asset_alerts_metric'].includes(task.type.value)">
                                <tr>
                                    <th>{{ trans('tasks.details.performance_metric.label') }}</th>
                                    <td>{{ task?.type?.label ?? '-' }}</td>
                                </tr>
                                <tr>
                                    <th>{{ trans('tasks.details.value.label') }}</th>
                                    <td>{{ task.payload?.value ?? '-' }}</td>
                                </tr>
                            </template>

                            <tr v-if="task.report">
                                <th>{{ trans('tasks.details.report.label') }}</th>
                                <td>{{ task.report }}</td>
                            </tr>

                            <tr>
                                <th class="pe-5 align-top">
                                    {{ trans('tasks.details.attachments.label') }}
                                </th>
                                <td>
                                    <div v-if="task.attachments.length">
                                        <div v-for="(file, idx) in task.attachments" :key="idx">
                                            <a :href="file.uri" target="_blank">{{ file.fileName }}</a>
                                        </div>
                                    </div>
                                    <div v-else>-</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <!-- Step Photos -->
            <section v-for="photo in task.payload?.stepPhotos" :key="photo.id" class="mt-4 p-3">
                <h2 class="mb-2 ps-2 !text-base !font-bold">{{ photo.label }}</h2>
                <div v-if="photo.attachment" class="px-2">
                    <a :href="photo.attachment.uri" class="block" target="_blank">
                        <img :alt="photo.label" :src="photo.attachment.uri" class="max-h-64 rounded-lg border border-gray-200 object-contain" />
                    </a>
                </div>
                <div v-else class="px-2 text-muted">-</div>
            </section>

            <!-- Steps -->
            <section v-if="task.payload?.steps?.length" class="p-3">
                <h2 class="mb-2 ps-2 !text-base !font-bold">{{ task.title }}</h2>

                <div v-for="group in task.payload.steps">
                    <h2 class="mb-2 ps-2 !text-base !font-bold">{{ group.group }}</h2>

                    <ul class="list-unstyled flex-column mx-2 flex flex-col gap-2">
                        <li v-for="step in group.steps.filter((s) => s.type !== 'photo')" :key="step.id">
                            <template v-if="step.type === 'number'">
                                <div>
                                    <span v-html="step.label" />: <span class="font-semibold">{{ step.result }}</span>
                                </div>
                            </template>
                            <template v-else-if="step.type === 'time'">
                                <div>
                                    <span v-html="step.label" />:
                                    <span class="font-semibold">{{ typeof step.result === 'string' && step.result ? step.result : '-' }}</span>
                                </div>
                            </template>
                            <template v-else-if="step.type === 'location'">
                                <div class="flex items-center gap-2">
                                    <check-circle-icon active class="h-5 w-5" />
                                    <div>
                                        <span v-html="step.label" />:&nbsp;
                                        <template v-if="isLocationUpdated(step)">
                                            <span style="text-decoration: line-through">{{ getLocationHierarchy(step.oldLocation) }}</span>
                                            &nbsp;&nbsp;→&nbsp;&nbsp;
                                            {{ getLocationHierarchy(step.newLocation) }}
                                        </template>
                                        <template v-else>
                                            {{ getLocationHierarchy(step.newLocation ?? step.oldLocation) }}
                                        </template>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="step.type === 'safety_inspection'">
                                <div class="flex items-center gap-2">
                                    <check-circle-icon v-if="step.result === true" active class="h-5 w-5" />
                                    <x-circle-icon v-else-if="step.result === false" active class="h-5 w-5" />
                                    <skipped-circle-icon v-else class="h-5 w-5" />
                                    <div v-html="step.label" />
                                </div>
                                <ul
                                    v-if="step.result === false && step.selectedDeficiencyLabels?.length"
                                    class="mt-2 ml-8 list-disc space-y-1 pl-4 text-sm text-gray-700"
                                >
                                    <li v-for="label in step.selectedDeficiencyLabels" :key="label">{{ label }}</li>
                                </ul>
                            </template>
                            <template v-else>
                                <div class="flex items-center gap-2">
                                    <check-circle-icon v-if="step.result === true" active class="h-5 w-5" />
                                    <x-circle-icon v-else-if="step.result === false" active class="h-5 w-5" />
                                    <skipped-circle-icon v-else class="h-5 w-5" />
                                    <div v-html="step.label" />
                                </div>
                            </template>
                        </li>
                    </ul>
                </div>
            </section>

            <!-- Digital signature footer: proves the snapshot is a verified, tamper-evident record.
                 The collapsed line shows a short fingerprint; expanding reveals the full signature
                 so it can be read without dominating the view. -->
            <div v-if="task.signature" class="mt-6 px-3 text-xs text-muted" data-testid="task-snapshot-signature">
                <div class="flex items-center gap-1.5">
                    <check-circle-icon active class="h-4 w-4" />
                    <span>{{ trans('tasks.details.signature.status') }}</span>
                </div>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span class="font-mono">
                        {{
                            trans('tasks.details.signature.fingerprint', {
                                fingerprint: task.signature.fingerprint,
                                algorithm: task.signature.algorithm,
                            })
                        }}
                    </span>
                    <button
                        type="button"
                        class="inline-flex items-center gap-0.5 text-primary hover:underline"
                        data-testid="task-snapshot-signature-toggle"
                        @click="signatureExpanded = !signatureExpanded"
                    >
                        {{ signatureExpanded ? trans('tasks.details.signature.hide_full') : trans('tasks.details.signature.show_full') }}
                        <icon :key="signatureExpanded" :name="signatureExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'" class="h-3 w-3" />
                    </button>
                </div>

                <div v-if="signatureExpanded" class="mt-1.5" data-testid="task-snapshot-signature-full">
                    <code class="block rounded bg-gray-100 p-2 font-mono break-all !text-gray-600 select-all">{{ task.signature.value }}</code>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { replaceModal } from '@/Utils/visitModal';
import Modal from '@/Components/Modal.vue';
import Alert from '@/Components/Alert.vue';
import { trans } from '@/mixins/i18n.js';
import { DateTime } from 'luxon';
import QrcodeVue from 'qrcode.vue';
import TaskApprovalForm from '@/Components/TaskApprovalForm.vue';
import AssetStatusLabel from '@/Components/AssetStatusLabel.vue';
import CheckCircleIcon from '@/Components/Icons/CheckCircleIcon.vue';
import XCircleIcon from '@/Components/Icons/XCircleIcon.vue';
import SkippedCircleIcon from '@/Components/Icons/SkippedCircleIcon.vue';
import { eventBus, events } from '@/eventBus.js';

const props = defineProps({
    task: Object,
    approvers: Object,
    performer: Object,
    approverId: String,
    canApprove: { type: Boolean, default: false },
    allowApproverSelection: { type: Boolean, default: true },
    createWorkOrderOnApproval: Boolean,
});

const modalRef = ref(null);
const instance = getCurrentInstance();

const signatureExpanded = ref(false);

const assetData = computed(() => {
    return [
        {
            label: trans('tasks.details.petitec_id.label'),
            value: props.task.asset.curieId,
        },
        {
            label: trans('tasks.details.asset_type.label'),
            value: props.task.asset.type.shortLabel,
        },
        {
            label: trans('tasks.details.designation.label'),
            value: props.task.asset.designation,
        },
        {
            label: trans('tasks.details.serial_number.label'),
            value: props.task.asset.serialNumber,
        },
        {
            label: trans('tasks.details.location.label'),
            value: props.task.asset.location?.hierarchy?.find((loc) => loc.level === 0)?.name ?? '-',
        },
        {
            label: trans('tasks.details.specific_location.label'),
            value: props.task.asset.location?.hierarchy?.find((loc) => loc.level === 1)?.name ?? '-',
        },
        {
            label: trans('tasks.details.area.label'),
            value:
                props.task.asset.location?.hierarchy?.find((loc) => loc.level === 3)?.name ??
                props.task.asset.location?.hierarchy?.find((loc) => loc.level === 2)?.name ??
                '-',
        },
        {
            label: trans('tasks.details.subarea.label'),
            value: '-',
        },
    ];
});

const onTaskApproved = (response) => {
    eventBus.$emit(events.taskApproved);

    const workorderEditUrl = response?.links?.workorder?.edit;

    if (!workorderEditUrl) {
        modalRef.value?.close();
        return;
    }

    replaceModal(modalRef.value, workorderEditUrl);
};

const openWorkOrder = (url) => {
    replaceModal(modalRef.value, url);
};

const getLocationHierarchy = (location) => {
    return (location || [])
        .map((loc) => (!loc.iata_code ? loc.name : loc.iata_code))
        .reverse()
        .join(', ');
};

const isLocationUpdated = (step) => {
    if (!step.oldLocation?.length || !step.newLocation?.length) {
        return false;
    }

    return getLocationHierarchy(step.oldLocation) !== getLocationHierarchy(step.newLocation);
};

const taskTimezone = computed(() => props.task.timezone ?? 'UTC');
const localeForFormat = computed(() => instance?.proxy?.currentLocale || 'en');

function formatTaskDate(iso) {
    if (!iso) return '';

    return DateTime.fromISO(iso, { zone: 'utc' }).setZone(taskTimezone.value).setLocale(localeForFormat.value).toLocaleString(DateTime.DATE_MED);
}

function formatTaskTime(iso) {
    if (!iso) return '';

    return DateTime.fromISO(iso, { zone: 'utc' }).setZone(taskTimezone.value).toFormat('HH:mm');
}

function formatRelatedWoTime(iso) {
    if (!iso) return '';
    const tz = props.task.relatedWorkOrder?.timezone ?? taskTimezone.value;
    const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(tz).setLocale(localeForFormat.value);

    return `${dt.toLocaleString(DateTime.DATE_MED)}, ${dt.toFormat('HH:mm')}`;
}
</script>

<style scoped>
.table-unstyled th,
.table-unstyled td {
    text-align: left;
    font-weight: 400;
    padding: 4px 8px !important;
}
</style>
